import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { FormControl } from '@mui/material'
import breakpoints from '../../constants/breakpoints'
import Form from '../../components/Form'
import Readspeaker from '../../components/Readspeaker'
import SlideContainer from '../../components/Layout/SlideContainer'
import IconChecked from '../../assets/checked.svg'
import IconCancel from '../../assets/cancel.svg'
import { Answer as AnswerType, ConditionalRouteType, Info, Slide } from '../../types'
import i18n from '../../i18n'
import { ModalConsumer } from '../../context/ModalContext'
import { getLocalStorage, getNextSkipRoute, setLocalStorage, setPivotPoint } from '../../utils'

interface Props {
  routeId: string
  basePath: string
  nextLink: string
  subtitle: string
  description?: string
  info?: Info[]
  title: string
  layout?: string
  answers?: AnswerType[]
  type: string
  routeIndex?: string
  routes: Slide[]
  onSkipRoute: (path: string) => void
  getNextSlidePath?: (id: string) => string
}

const findLocalStorageAnswer = (routeId: string): string => {
  const foundAnswer = getLocalStorage('answers').find((answer) => answer.routeId === routeId)

  return foundAnswer ? foundAnswer.answer[0] : ''
}

const ClosedQuestion: React.FC<Props> = ({
  basePath,
  routeId,
  routeIndex,
  routes,
  nextLink,
  subtitle,
  description,
  info,
  title,
  layout,
  answers,
  type,
  getNextSlidePath,
  onSkipRoute,
}) => {
  useEffect(() => {
    const nextRoute = routeIndex && getNextSkipRoute(routeIndex, routes)
    nextRoute && onSkipRoute(nextRoute.path)
  }, [])

  const [selectedValue, setSelectedValue] = useState(findLocalStorageAnswer(routeId))
  const ref = useRef<HTMLDivElement>(null)

  const handleClick = (
    input: string,
    isConditionalRoute = false,
    pivotPoint?: ConditionalRouteType[] | ConditionalRouteType,
  ) => {
    setSelectedValue(input)

    let answer = ''
    switch (input) {
      case 'Eens':
        answer = 'Ja'
        break
      case 'Oneens':
        answer = 'Nee'
        break
      default:
        answer = input
        break
    }

    setLocalStorage({ routeId, answer: [answer], isConditionalRoute }, basePath)

    if (pivotPoint) setPivotPoint(pivotPoint)
  }

  return (
    <SlideContainer>
      <Content className="text-formatted">
        <FixedWidth>
          <Subtitle>{subtitle}</Subtitle>
          <h1 id={title.replace(/[^A-Z0-9]+/gi, '_')}>{title}</h1>
          <Readspeaker />
        </FixedWidth>
        <ButtonsContainer $unique={type === 'uniqueQuestion'}>
          {(() => {
            switch (type) {
              case 'statement':
                return (
                  <FormItem component="fieldset">
                    <legend className="sr-only">{title}</legend>
                    <RadioGroup ref={ref}>
                      <Form.RadioButton
                        active={selectedValue === i18n.t('answers.yes')}
                        onClick={(input) => handleClick(input)}
                        to={nextLink}
                        value={i18n.t('answers.agree')}
                        iconName={IconChecked}
                      />
                      <Form.RadioButton
                        active={selectedValue === i18n.t('answers.no')}
                        onClick={(input) => handleClick(input)}
                        to={nextLink}
                        value={i18n.t('answers.disagree')}
                        iconName={IconCancel}
                      />
                      <Form.RadioButton
                        active={selectedValue === i18n.t('answers.dontKnow')}
                        onClick={(input) => handleClick(input)}
                        to={nextLink}
                        value={i18n.t('answers.dontKnow')}
                      />
                    </RadioGroup>
                  </FormItem>
                )
              case 'uniqueQuestion':
                if (answers) {
                  return (
                    <FormItem component="fieldset" $unique={type === 'uniqueQuestion'}>
                      <legend className="sr-only">{title}</legend>
                      <RadioGroup
                        direction={layout === 'vertical' ? 'column' : 'row'}
                        grid={answers.length > 3 ? true : false}
                        ref={ref}
                      >
                        {answers.map((answer: AnswerType) => {
                          return (
                            <Form.RadioButton
                              key={`uniqueQuestion-${answer.title}`}
                              active={selectedValue === answer.title}
                              onClick={(input) => {
                                const isConditionalRoute =
                                  !!answer.conditionalRoute || !!answer.nextRouteId
                                handleClick(input, isConditionalRoute, answer.conditionalRoute)
                              }}
                              to={
                                answer.nextRouteId
                                  ? getNextSlidePath
                                    ? getNextSlidePath(answer.nextRouteId)
                                    : nextLink
                                  : nextLink
                              }
                              value={answer.title}
                            />
                          )
                        })}
                      </RadioGroup>
                    </FormItem>
                  )
                }
                break
              case 'defaultQuestion':
                return (
                  <FormItem component="fieldset">
                    <legend className="sr-only">{title}</legend>
                    <RadioGroup>
                      <Form.RadioButton
                        active={selectedValue === i18n.t('answers.yes')}
                        onClick={(input) => handleClick(input)}
                        to={nextLink}
                        value={i18n.t('answers.yes')}
                        iconName={IconChecked}
                      />
                      <Form.RadioButton
                        active={selectedValue === i18n.t('answers.no')}
                        onClick={(input) => handleClick(input)}
                        to={nextLink}
                        value={i18n.t('answers.no')}
                        iconName={IconCancel}
                      />
                    </RadioGroup>
                  </FormItem>
                )
            }
          })()}
        </ButtonsContainer>
        {description && (
          <Description dangerouslySetInnerHTML={{ __html: description }}></Description>
        )}
        {info &&
          info.map((item, index) => (
            <ModalConsumer key={index}>
              {({ openModal }) => (
                <StyledLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    openModal(item.title, item.content)
                  }}
                >
                  {item.urlText ? item.urlText : i18n.t('explanation.moreInfo')}
                </StyledLink>
              )}
            </ModalConsumer>
          ))}
      </Content>
    </SlideContainer>
  )
}

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;
`

const Description = styled.div`
  margin-top: 72px;
`

const FormItem = styled(FormControl)<{ component: any; $unique?: boolean }>`
  width: ${({ $unique }) => $unique && '100%'};
`

const StyledLink = styled.a`
  color: ${({ theme }) => theme.blue};
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.blue};
  }
`

const FixedWidth = styled.div`
  max-width: 700px;
`

const Subtitle = styled.span`
  font-size: 1rem;
  font-weight: bold;
`

const ButtonsContainer = styled.div<{ $unique?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $unique }) => $unique && '100%'};

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`

const RadioGroup = styled.div<{ direction?: 'column' | 'row'; grid?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${breakpoints.md}) {
    div {
      width: 100%;
    }
  }

  @media (min-width: ${breakpoints.md}) {
    flex-direction: ${({ direction }) => direction || 'row'};
    align-items: stretch;
    justify-content: center;

    > div {
      width: 30%;
    }

    label {
      width: 100%;
      margin-top: 0;
      padding: 17px 30px;
    }

    ${({ direction }) =>
      direction &&
      direction === 'column' &&
      `
        div {
          margin: 4px 0;
          min-height: 100px;
          width: 100%;
        }
    `}

    ${({ grid }) =>
      grid &&
      `
        flex-wrap: wrap;
        justify-content: flex-start;
      `}
  }
`

export default ClosedQuestion
